import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Box, Button, Chip, Grid2, MenuItem, Checkbox, Typography, CircularProgress } from '@mui/material'
import { GRAY_COLORS, INFO_COLORS } from 'asterX-UI/colors.constant'
import { getValidDate } from 'lib/utils'
import { useHomeStore } from 'context/HomeStore'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { enqueueSnackbar } from 'notistack'
import { NotFound } from 'components/NotFound/NotFound'
import { debounce } from 'lodash'
import ActionModal from '../../../asterX-UI/Modals/ActionModal'

const StandardTable = lazy(() => import('asterX-UI/Table/StandardTable'))
const initPagination = { limit: 100, offset: 0, count: 0 }

const ExportClientBankModal = ({ open, onClose = () => {} }) => {
  const {
    homeStore: { banks },
    homeStoreFunc: { getContractList }
  } = useHomeStore()

  const [exportListData, setExportListData] = useState([])
  const [bankBin, setBankBin] = useState(null)
  const [pagination, setPagination] = useState(initPagination)
  const [selectedRows, setSelectedRows] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  const getContractListData = async () => {
    const { list, count } = await getContractList(
      { bankBin, statusCode: 'userSetDDS' },
      pagination.limit,
      pagination.offset
    )
    setExportListData(list)
    setPagination(prevState => ({ ...prevState, count }))
  }

  const resetState = () => {
    onClose()
    setExportListData(null)
    setBankBin(null)
    setPagination(initPagination)
    setSelectedRows([])
    setSelectAll(false)
  }

  useEffect(() => {
    if (selectedRows?.length !== exportListData?.length) {
      setSelectAll(false)
    } else if (exportListData?.length > 0) {
      setSelectAll(true)
    }
  }, [selectedRows, exportListData])

  const handleClose = () => {
    resetState()
  }

  const handleSearch = debounce(async () => {
    getContractListData()
  }, 300)

  const handleChange = event => {
    setBankBin(event.target.value)
    setExportListData([])
    setSelectedRows([])
    setSelectAll(false)
  }

  const handleSelectAll = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)
    setSelectedRows(newSelectAll ? exportListData.map(item => item?.loanApplication?.id) : [])
  }

  const handleRowSelect = id => {
    setSelectedRows(prevSelected =>
      prevSelected.includes(id) ? prevSelected.filter(rowId => rowId !== id) : [...prevSelected, id]
    )
  }

  const handleConfirm = async () => {
    const data = {
      loanApplicationIdList: selectedRows
    }

    if (selectedRows.length === 0) {
      enqueueSnackbar('Выберите из списка', { variant: 'info' })
      return
    }

    try {
      const res = await CreditFlow.generateDdsFile(data)
      if (res?.data) {
        enqueueSnackbar('Успешно', { variant: 'success' })
        const blob = new Blob([res.data], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '1CClientBank.txt'
        link.click()
        setTimeout(() => window.URL.revokeObjectURL(url), 3000)
        resetState()
      } else {
        enqueueSnackbar('Ошибка при обработке файла', { variant: 'error' })
      }
    } catch (error) {
      console.error('Ошибка:', error)
      enqueueSnackbar('Ошибка при выполнении запроса', { variant: 'error' })
    }
  }

  const tableData = {
    columns: [
      <Box display="flex" gap="10px" alignItems="center">
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all' }}
          sx={{ margin: 0, padding: 0 }}
        />
        <Typography>№</Typography>
      </Box>,
      'Клиент',
      'Продукт',
      'Сумма займа',
      'Дата создания',
      'Статус'
    ],
    rows: exportListData?.map(item => {
      const isChecked = selectedRows.includes(item?.loanApplication?.id)
      return [
        <Box display="flex" gap="10px" alignItems="center">
          <Checkbox
            checked={isChecked}
            onChange={() => handleRowSelect(item?.loanApplication?.id)}
            inputProps={{ 'aria-label': `select row ${item?.loanApplication?.id}` }}
            sx={{ margin: 0, padding: 0 }}
          />
          <Typography sx={{ color: INFO_COLORS[700] }}>{item?.id || '-'}</Typography>
        </Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplication?.clientFullName || '-'}</Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplication?.creditProduct?.name || '-'}</Box>,
        item?.loanApplication?.loanAmount?.toString()?.thousandSeparator() || '-',
        getValidDate(item?.loanApplication?.createdDate, true),
        <Chip
          size="small"
          label={item?.loanApplication?.status?.name || item?.loanApplication?.status?.code || 'Заявка авторизована'}
          variant="outlined"
          sx={{ borderRadius: '6px', fontSize: '12px' }}
        />
      ]
    })
  }

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      title="Выгрузить банк клиента"
      labelConfirm="Выгрузить"
      maxWidth="lg"
      disableConfirm={selectedRows.length === 0}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
          minHeight: '200px',
          marginTop: '5px'
        }}
      >
        <Grid2 container sx={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%' }}>
          <Grid2 size="grow">
            <SelectPlaceholder
              IconComponent={props => <ExpandMoreIcon {...props} />}
              placeholder="Выберите банк клиента"
              name="bankBin"
              value={bankBin || ''}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="">Не выбрано</MenuItem>
              {banks?.map(item => (
                <MenuItem value={item?.bin} key={item?.id}>
                  {item?.name}
                </MenuItem>
              ))}
            </SelectPlaceholder>
          </Grid2>
          <Grid2 size={{ xs: 2 }}>
            <Button
              sx={{ fontSize: 14, fontWeight: 600 }}
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleSearch}
              disabled={!bankBin}
            >
              Найти
            </Button>
          </Grid2>
        </Grid2>
        {exportListData?.length > 0 ? (
          <Suspense
            fallback={
              <Box display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
              </Box>
            }
          >
            <StandardTable
              tableData={tableData}
              tableStyles={{ border: `1px solid ${GRAY_COLORS[200]}` }}
              btnStyle={{ padding: 0, border: 'none' }}
            />
          </Suspense>
        ) : (
          <NotFound title="Укажите банк и нажмите Найти" fontSize="18px" />
        )}
      </Box>
    </ActionModal>
  )
}

export default ExportClientBankModal
